/**
 * Page back-office : gestion des comptes parents inscrits.
 * - Liste filtrable (tous / validés / bloqués / en attente / en ligne)
 * - Bloquer / débloquer avec raison
 * - Supprimer (admin uniquement)
 * - KPIs : total, validés, en ligne (< 10 min), actifs 24h/7j
 */
import { Fragment, useState } from 'react';
import {
  Users, Search, Lock, Unlock, Trash2, X, Loader2, Phone, Mail,
  Circle, AlertCircle, Filter, BarChart3, CheckCircle2, UserX,
  ChevronRight, ChevronDown, GraduationCap, Link as LinkIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import {
  useListerParentsQuery,
  useBloquerParentMutation,
  useSupprimerParentMutation,
  type ParentItem,
} from '../../services/parentsApi';

type Statut = 'tous' | 'valides' | 'bloques' | 'en_attente' | 'en_ligne';

const STATUT_LABELS: Record<Statut, string> = {
  tous: 'Tous',
  valides: 'Validés',
  bloques: 'Bloqués',
  en_attente: 'En attente',
  en_ligne: 'En ligne',
};

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function fmtDelai(s: string | null): { txt: string; cls: string } {
  if (!s) return { txt: 'jamais', cls: 'text-gray-400' };
  const diff = Date.now() - new Date(s).getTime();
  const min = diff / 60000;
  if (min < 10) return { txt: 'en ligne', cls: 'text-emerald-500' };
  if (min < 60) return { txt: `il y a ${Math.floor(min)} min`, cls: 'text-emerald-600' };
  const h = min / 60;
  if (h < 24) return { txt: `il y a ${Math.floor(h)} h`, cls: 'text-amber-600' };
  const j = h / 24;
  if (j < 7) return { txt: `il y a ${Math.floor(j)} j`, cls: 'text-gray-500' };
  if (j < 30) return { txt: `il y a ${Math.floor(j / 7)} sem.`, cls: 'text-gray-500' };
  return { txt: `il y a ${Math.floor(j / 30)} mois`, cls: 'text-gray-400' };
}

export default function Parents() {
  const navigate = useNavigate();
  const role = useSelector((s: RootState) => s.auth.user?.role || '');
  const canDelete = role === 'admin' || role === 'super_admin';

  const [statut, setStatut] = useState<Statut>('tous');
  const [search, setSearch] = useState('');
  const [modalBloquer, setModalBloquer] = useState<ParentItem | null>(null);
  const [modalSupprimer, setModalSupprimer] = useState<ParentItem | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const { data, isFetching, refetch } = useListerParentsQuery(
    { statut, search: search.trim() || undefined },
    { pollingInterval: 30_000 },  // refresh KPI en ligne toutes les 30s
  );
  const [bloquer, { isLoading: blocking }] = useBloquerParentMutation();
  const [supprimer, { isLoading: deleting }] = useSupprimerParentMutation();

  const handleDebloquer = async (p: ParentItem) => {
    if (!window.confirm(`Débloquer le compte de ${p.prenoms} ${p.nom} ?`)) return;
    try {
      await bloquer({ id: p.id, action: 'debloquer' }).unwrap();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users size={22} className="text-blue-600 dark:text-blue-400" />
            Comptes parents
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Liste des parents inscrits sur l'application. Vous pouvez bloquer un compte
            (le parent ne pourra plus se connecter) ou le supprimer définitivement.
          </p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
          {isFetching ? '…' : '↻ Actualiser'}
        </button>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <Kpi label="Total inscrits" valeur={data?.kpis.total ?? '—'} couleur="blue"
          icon={<Users size={14} />} />
        <Kpi label="En ligne" valeur={data?.kpis.en_ligne ?? '—'} couleur="emerald"
          icon={<Circle size={10} fill="currentColor" />} sublabel="(< 10 min)" />
        <Kpi label="Actifs 24h" valeur={data?.kpis.actifs_24h ?? '—'} couleur="cyan"
          icon={<BarChart3 size={14} />} />
        <Kpi label="Bloqués" valeur={data?.kpis.bloques ?? '—'} couleur="red"
          icon={<Lock size={14} />} />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded p-1">
          {(['tous', 'valides', 'en_ligne', 'en_attente', 'bloques'] as Statut[]).map(s => (
            <button key={s} onClick={() => setStatut(s)}
              className={`px-3 py-1 text-xs rounded ${
                statut === s
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}>
              {STATUT_LABELS[s]}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, email, téléphone…"
            className="pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-56" />
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isFetching && !data ? (
          <div className="p-10 text-center text-gray-500">
            <Loader2 size={20} className="inline animate-spin" />
          </div>
        ) : (data?.results || []).length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <Users size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-400" />
            Aucun parent ne correspond aux filtres.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="w-6"></th>
                  <th className="text-left px-3 py-2">Parent</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">Contact</th>
                  <th className="text-center px-3 py-2">Enfants</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Inscrit</th>
                  <th className="text-left px-3 py-2">Activité</th>
                  <th className="text-center px-3 py-2">Statut</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {data?.results.map(p => {
                  const delai = fmtDelai(p.derniere_activite);
                  const isOpen = expanded.has(p.id);
                  const canExpand = p.nb_enfants > 0;
                  return (
                    <Fragment key={p.id}>
                      <tr
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${canExpand ? 'cursor-pointer' : ''}`}
                        onClick={() => canExpand && toggleExpand(p.id)}>
                        <td className="pl-2 pr-1 text-gray-400">
                          {canExpand ? (
                            isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {p.prenoms} {p.nom}
                          </div>
                          <div className="text-[10px] text-gray-500 sm:hidden">
                            {p.email || p.telephone}
                          </div>
                        </td>
                        <td className="px-3 py-2 hidden sm:table-cell text-xs text-gray-600 dark:text-gray-400">
                          {p.email && (
                            <div className="flex items-center gap-1">
                              <Mail size={11} className="opacity-60" />
                              <span className="truncate max-w-[180px]">{p.email}</span>
                            </div>
                          )}
                          {p.telephone && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Phone size={11} className="opacity-60" />
                              {p.telephone}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                            p.nb_enfants === 0
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                              : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                          }`}>
                            {p.nb_enfants}
                          </span>
                        </td>
                        <td className="px-3 py-2 hidden md:table-cell text-xs text-gray-600 dark:text-gray-400">
                          {fmtDate(p.date_creation)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs ${delai.cls}`}>
                            {delai.txt === 'en ligne' && (
                              <Circle size={8} fill="currentColor" className="inline mr-1" />
                            )}
                            {delai.txt}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {p.bloque ? (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                              Bloqué
                            </span>
                          ) : !p.valide_inscription ? (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                              En attente
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                              Validé
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}>
                          {p.bloque ? (
                            <button onClick={() => handleDebloquer(p)} disabled={blocking}
                              title="Débloquer le compte"
                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded">
                              <Unlock size={14} />
                            </button>
                          ) : (
                            <button onClick={() => setModalBloquer(p)}
                              title="Bloquer le compte"
                              className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded">
                              <Lock size={14} />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => setModalSupprimer(p)}
                              title="Supprimer définitivement"
                              className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded ml-1">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && p.enfants.length > 0 && (
                        <tr className="bg-gray-50/60 dark:bg-gray-900/40">
                          <td></td>
                          <td colSpan={7} className="px-3 py-3">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 inline-flex items-center gap-1">
                              <LinkIcon size={11} /> Enfants liés ({p.enfants.length})
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                              {p.enfants.map(enf => (
                                <button key={enf.matricule}
                                  onClick={() => navigate(`/etudiants/${enf.matricule}/bilan`)}
                                  className="text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
                                  <div className="flex items-start gap-2">
                                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-700 dark:text-blue-300 flex-shrink-0">
                                      <GraduationCap size={14} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {enf.prenoms} {enf.nom}
                                      </div>
                                      <div className="text-[10px] text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2">
                                        <span className="font-mono">{enf.matricule}</span>
                                        {enf.classe && <span>·  {enf.classe}</span>}
                                      </div>
                                      <div className="text-[10px] text-gray-500 mt-0.5">
                                        Relation : <span className="font-medium">{enf.relation_label}</span>
                                      </div>
                                    </div>
                                    <ChevronRight size={12} className="text-gray-400 flex-shrink-0 self-center" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal bloquer */}
      {modalBloquer && (
        <ModalBloquer parent={modalBloquer} loading={blocking}
          onClose={() => setModalBloquer(null)}
          onConfirm={async (raison) => {
            try {
              await bloquer({ id: modalBloquer.id, action: 'bloquer', raison }).unwrap();
              setModalBloquer(null);
            } catch (e: any) {
              alert(e?.data?.detail || 'Erreur');
            }
          }} />
      )}

      {/* Modal supprimer */}
      {modalSupprimer && (
        <ModalSupprimer parent={modalSupprimer} loading={deleting}
          onClose={() => setModalSupprimer(null)}
          onConfirm={async (raison) => {
            try {
              await supprimer({ id: modalSupprimer.id, raison }).unwrap();
              setModalSupprimer(null);
            } catch (e: any) {
              alert(e?.data?.detail || 'Erreur');
            }
          }} />
      )}
    </div>
  );
}

function Kpi({ label, valeur, couleur, icon, sublabel }: {
  label: string; valeur: number | string;
  couleur: 'blue' | 'emerald' | 'cyan' | 'red';
  icon: React.ReactNode; sublabel?: string;
}) {
  const colorClasses: Record<string, string> = {
    blue:    'text-blue-600 dark:text-blue-400',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    cyan:    'text-cyan-600 dark:text-cyan-400',
    red:     'text-red-600 dark:text-red-400',
  };
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5">
      <div className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${colorClasses[couleur]}`}>
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{valeur}</div>
      {sublabel && <div className="text-[10px] text-gray-400 dark:text-gray-500">{sublabel}</div>}
    </div>
  );
}

function ModalBloquer({ parent, loading, onClose, onConfirm }: {
  parent: ParentItem; loading: boolean;
  onClose: () => void; onConfirm: (raison: string) => void;
}) {
  const [raison, setRaison] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Lock size={16} className="text-amber-600" /> Bloquer le compte
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={18} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Vous allez bloquer le compte de <strong>{parent.prenoms} {parent.nom}</strong>.
            Le parent ne pourra plus se connecter à l'application tant que le compte est bloqué.
          </p>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Raison (visible dans les logs) *
            </label>
            <textarea value={raison} onChange={(e) => setRaison(e.target.value)}
              rows={3} maxLength={300}
              placeholder="Ex : signalement de comportement abusif…"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            <p className="text-[10px] text-gray-500 mt-0.5">Minimum 5 caractères</p>
          </div>
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button onClick={() => onConfirm(raison)} disabled={loading || raison.trim().length < 5}
            className="px-4 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
            Bloquer
          </button>
        </footer>
      </div>
    </div>
  );
}

function ModalSupprimer({ parent, loading, onClose, onConfirm }: {
  parent: ParentItem; loading: boolean;
  onClose: () => void; onConfirm: (raison: string) => void;
}) {
  const [raison, setRaison] = useState('');
  const [confirme, setConfirme] = useState('');
  const motCle = `${parent.prenoms} ${parent.nom}`.trim().toUpperCase();
  const ok = confirme.trim().toUpperCase() === motCle;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-red-700 dark:text-red-400 inline-flex items-center gap-2">
            <UserX size={16} /> Supprimer définitivement
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={18} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300 inline-flex gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              Cette action est <strong>irréversible</strong>. Le compte de
              <strong> {parent.prenoms} {parent.nom}</strong> et ses {parent.nb_enfants} lien(s)
              enfant(s) seront supprimés. Les messages envoyés restent en archive.
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Raison (logs)
            </label>
            <input type="text" value={raison} onChange={(e) => setRaison(e.target.value)}
              maxLength={300} placeholder="Ex : doublon, demande utilisateur…"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Tapez <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-[11px]">{motCle}</code> pour confirmer
            </label>
            <input type="text" value={confirme} onChange={(e) => setConfirme(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 font-mono" />
          </div>
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button onClick={() => onConfirm(raison)} disabled={loading || !ok}
            className="px-4 py-1.5 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Supprimer définitivement
          </button>
        </footer>
      </div>
    </div>
  );
}
